import { useCallback, useEffect, useState } from "react";
import { Badge, Button, Card, Icon } from "@nelurio/ui";
import { useI18n } from "@nelurio/i18n";
import { api, isDemoMode } from "../api/client";
import { useCourses } from "../hooks/useCourses";

// NotebookLM-style workspace: a course's library resources are the sources,
// the answers are grounded ONLY in the pinned ones, with [Source N] citations.

interface NotebookSummary { id: string; title: string; sourceCount: number; updatedAt: string; }
interface NotebookSource { id: string; title: string; kind: string; sizeBytes: number; status: string; }
interface ResourceItem { id: string; title: string; kind: string; sizeBytes: number; status: string; }
interface NotebookMessage { id: string; role: string; text: string; sources?: SourceRef[] | null; generated?: boolean; saved?: string; }
interface SourceRef { title: string; resourceId?: string; pageNumber?: number; url?: string; }

/** Server codes carry the REAL reason — surface it instead of a generic
 *  «مشکلی پیش آمد» (the owner hit this when sources were failed/not-ready). */
function notebookErrorText(err: unknown, t: (key: string) => string): string {
  if (err && typeof err === "object" && "code" in err) {
    const code = (err as { code?: string }).code;
    if (code === "resource_mismatch") return t("notebook.errSource");
    if (code === "too_large") return t("notebook.errFile");
    if (code === "ocr_failed") return t("notebook.errFile");
  }
  return t("common.error");
}

function formatSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return (bytes / (1024 * 1024)).toFixed(1) + " MB";
  if (bytes >= 1024) return Math.round(bytes / 1024) + " KB";
  return String(bytes);
}

export function NotebookView() {
  const { t, locale } = useI18n();
  const { courses } = useCourses();
  const [courseId, setCourseId] = useState("");
  const [notebooks, setNotebooks] = useState<NotebookSummary[]>([]);
  const [activeId, setActiveId] = useState("");
  const [sources, setSources] = useState<NotebookSource[]>([]);
  const [library, setLibrary] = useState<ResourceItem[]>([]);
  const [messages, setMessages] = useState<NotebookMessage[]>([]);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [creating, setCreating] = useState(false);
  const [newTitle, setNewTitle] = useState("");

  const activeNotebook = notebooks.find((notebook) => notebook.id === activeId) || null;

  // Notebooks of the selected course; remember the last-open one per course.
  useEffect(() => {
    setActiveId("");
    setNotebooks([]);
    setSources([]);
    setMessages([]);
    if (!courseId || isDemoMode()) return;
    let stale = false; // A1/A4: a slow response for a previous course must never land
    api<NotebookSummary[]>(`/ai/notebooks?courseID=${courseId}`).then((items) => {
      if (stale) return;
      setNotebooks(Array.isArray(items) ? items : []);
      const last = window.localStorage.getItem(`nelurio.notebook.last.${courseId}`);
      if (last && items.some((notebook) => notebook.id === last)) setActiveId(last);
    }).catch(() => { if (!stale) setNotice(t("common.error")); });
    return () => { stale = true; };
  }, [courseId, t]);

  // The course library = the source picker universe.
  useEffect(() => {
    setLibrary([]);
    if (!courseId || isDemoMode()) return;
    api<ResourceItem[]>(`/resources?courseId=${courseId}`)
      .then((items) => setLibrary(Array.isArray(items) ? items : []))
      .catch(() => undefined);
  }, [courseId]);

  const openNotebook = useCallback((id: string) => {
    setActiveId(id);
    setMessages([]);
    if (courseId) window.localStorage.setItem(`nelurio.notebook.last.${courseId}`, id);
    api<{ id: string; title: string; sources: NotebookSource[] }>(`/ai/notebooks/${id}`)
      .then((detail) => setSources(Array.isArray(detail.sources) ? detail.sources : []))
      .catch(() => setNotice(t("common.error")));
    api<NotebookMessage[]>(`/ai/notebooks/${id}/messages`)
      .then((items) => setMessages(Array.isArray(items) ? items.map((message) => ({ ...message, text: message.text ?? (message as unknown as { content: string }).content })) : []))
      .catch(() => undefined);
  }, [courseId, t]);

  const createNotebook = async () => {
    const title = newTitle.trim() || t("notebook.defaultTitle");
    const initialSources = library.filter((item) => item.status === "ready").slice(0, 1);
    if (!courseId || initialSources.length === 0) {
      setNotice(t("notebook.needSource"));
      return;
    }
    setNotice("");
    try {
      const created = await api<{ id: string }>("/ai/notebooks", { method: "POST", body: JSON.stringify({ courseId, title, sourceIds: initialSources.map((item) => item.id) }) });
      const list = await api<NotebookSummary[]>(`/ai/notebooks?courseID=${courseId}`);
      setNotebooks(Array.isArray(list) ? list : []);
      setCreating(false);
      setNewTitle("");
      openNotebook(created.id);
    } catch (reason) {
      setNotice(notebookErrorText(reason, t));
    }
  };

  // Toggle a library resource in/out of the notebook's grounding set.
  const toggleSource = async (resourceId: string) => {
    if (!activeId) return;
    const has = sources.some((source) => source.id === resourceId);
    if (!has && sources.length === 0 && !library.some((item) => item.id === resourceId)) return;
    const next = has ? sources.filter((source) => source.id !== resourceId) : [...sources, { id: resourceId, title: library.find((item) => item.id === resourceId)?.title || "", kind: library.find((item) => item.id === resourceId)?.kind || "file", sizeBytes: library.find((item) => item.id === resourceId)?.sizeBytes || 0, status: "ready" }];
    if (next.length === 0) {
      setNotice(t("notebook.needSource"));
      return;
    }
    const previous = sources;
    setSources(next);
    try {
      await api(`/ai/notebooks/${activeId}`, { method: "PATCH", body: JSON.stringify({ sourceIds: next.map((source) => source.id) }) });
    } catch {
      setSources(previous);
      setNotice(t("common.error"));
    }
  };

  const send = async () => {
    const message = draft.trim();
    if (!message || !activeId || busy) return;
    setBusy(true);
    setNotice("");
    setDraft("");
    const sentTo = activeId; // A2: answers must never land in another notebook
    const localId = `local-${Date.now()}`;
    setMessages((items) => [...items, { id: localId, role: "user", text: message }]);
    try {
      const reply = await api<{ text: string; sources?: SourceRef[] }>(`/ai/notebooks/${sentTo}/chat`, { method: "POST", body: JSON.stringify({ message, locale }) });
      if (sentTo === activeId) setMessages((items) => [...items, { id: `${localId}-a`, role: "assistant", text: reply.text, sources: reply.sources }]);
    } catch (reason) {
      setMessages((items) => [...items, { id: `${localId}-e`, role: "assistant", text: notebookErrorText(reason, t) }]);
    } finally {
      setBusy(false);
    }
  };

  const generate = async (kind: "summary" | "briefing" | "faq" | "studyguide") => {
    if (!activeId || busy) return;
    setBusy(true);
    setNotice("");
    try {
      const result = await api<{ text: string; sources?: SourceRef[] }>(`/ai/notebooks/${activeId}/generate`, { method: "POST", body: JSON.stringify({ kind, locale }) });
      setMessages((items) => [...items, { id: `gen-${Date.now()}`, role: "assistant", text: result.text, sources: result.sources, generated: true }]);
    } catch {
      setNotice(t("common.error"));
    } finally {
      setBusy(false);
    }
  };

  const deleteNotebook = async (id: string) => {
    try {
      await api(`/ai/notebooks/${id}`, { method: "DELETE" });
      setNotebooks((items) => items.filter((notebook) => notebook.id !== id));
      if (activeId === id) {
        setActiveId("");
        setSources([]);
        setMessages([]);
      }
    } catch {
      setNotice(t("common.error"));
    }
  };

  const pinned = new Set(sources.map((source) => source.id));

  const saveAnswerToCourse = async (message: NotebookMessage) => {
    if (!courseId || message.saved || message.role !== "assistant") return;
    try {
      const result = await api<{ flashcards: number; quiz: number }>("/ai/extract", { method: "POST", body: JSON.stringify({ courseId, text: message.text, locale }) });
      setMessages((items) => items.map((item) => (item.id === message.id ? { ...item, saved: t("study.extracted", { cards: result.flashcards, quiz: result.quiz }) } : item)));
    } catch (reason) {
      setNotice(notebookErrorText(reason, t));
    }
  };

  return (
    <div className="notebook">
      <Card className="notebook__setup" elevated>
        <div className="notebook__setup-row">
          <label className="notebook__setup-label" htmlFor="notebook-course">{t("notebook.course")}</label>
          <select id="notebook-course" className="nl-select notebook__course" value={courseId} onChange={(event) => setCourseId(event.target.value)}>
            <option value="" disabled hidden>{t("notebook.coursePlaceholder")}</option>
            {courses.map((course) => (
              <option key={course.id} value={course.id}>{course.title}</option>
            ))}
          </select>
          {activeNotebook && <Button className="notebook__new-btn" onClick={() => setCreating(true)}>{t("notebook.new")}</Button>}
        </div>

        {courseId && notebooks.length > 0 && (
          <div className="notebook__chips">
            {notebooks.map((notebook) => (
              <div key={notebook.id} className={`notebook__chip ${notebook.id === activeId ? "is-active" : ""}`}>
                <button className="notebook__chip-open" onClick={() => openNotebook(notebook.id)}>
                  <Icon name="book" size={14} />
                  <span>{notebook.title}</span>
                  <Badge tone="purple">{notebook.sourceCount}</Badge>
                </button>
                <button className="notebook__chip-delete" aria-label={t("common.delete")} onClick={() => void deleteNotebook(notebook.id)}>
                  <Icon name="trash" size={13} />
                </button>
              </div>
            ))}
          </div>
        )}

        {courseId && !creating && notebooks.length === 0 && (
          <div className="notebook__empty">
            <p>{t("notebook.emptyBody")}</p>
            <Button icon="plus" onClick={() => setCreating(true)}>{t("notebook.new")}</Button>
          </div>
        )}

        {creating && (
          <div className="notebook__create">
            <input className="nl-input notebook__title-input" value={newTitle} onChange={(event) => setNewTitle(event.target.value)} placeholder={t("notebook.titlePlaceholder")} aria-label={t("notebook.titlePlaceholder")} />
            <Button onClick={() => void createNotebook()}>{t("common.save")}</Button>
            <Button onClick={() => setCreating(false)}>{t("common.cancel")}</Button>
          </div>
        )}
        {notice && <p className="notebook__notice" role="alert">{notice}</p>}
      </Card>

      {activeNotebook && (
        <div className="notebook__layout">
          <Card className="notebook__sources" elevated>
            <div className="notebook__panel-head">
              <strong>{t("notebook.sources")}</strong>
              <Badge tone="purple">{sources.length}</Badge>
            </div>
            {library.length === 0 && <p className="notebook__hint">{t("notebook.libraryEmpty")}</p>}
            {library.map((item) => {
              const ready = item.status === "ready";
              return (
                <label key={item.id} className={`notebook__source-row ${ready ? "" : "is-disabled"}`}>
                  <input
                    type="checkbox"
                    className="notebook__source-check"
                    checked={pinned.has(item.id)}
                    disabled={!ready}
                    onChange={() => void toggleSource(item.id)}
                  />
                  <span className="notebook__source-body">
                    <strong>{item.title}</strong>
                    <small>{ready ? formatSize(item.sizeBytes) : t("notebook.sourceNotReady")}</small>
                  </span>
                  <Badge tone="blue">{item.kind}</Badge>
                </label>
              );
            })}
          </Card>

          <Card className="notebook__chat" elevated>
            <div className="notebook__panel-head">
              <strong>{activeNotebook.title}</strong>
              <div className="notebook__tools">
                <button type="button" className="notebook__tool" disabled={busy} onClick={() => void generate("summary")}>{t("notebook.genSummary")}</button>
                <button type="button" className="notebook__tool" disabled={busy} onClick={() => void generate("briefing")}>{t("notebook.genBriefing")}</button>
                <button type="button" className="notebook__tool" disabled={busy} onClick={() => void generate("faq")}>{t("notebook.genFaq")}</button>
                <button type="button" className="notebook__tool" disabled={busy} onClick={() => void generate("studyguide")}>{t("notebook.genStudy")}</button>
              </div>
            </div>
            <div className="notebook__stream">
              {messages.length === 0 && (
                <p className="notebook__hint">{t("notebook.chatHint")}</p>
              )}
              {messages.map((message) => (
                <article key={message.id} className={`chat-message ${message.role === "user" ? "chat-message--user" : "chat-message--assistant"}`}>
                  <div className="chat-message__bubble">
                    {message.generated && <Badge tone="purple">{t("notebook.generatedBadge")}</Badge>}
                    <p>{message.text}</p>
                    {message.role === "assistant" && !message.generated && (
                      <button type="button" className="widget-card__cta" onClick={() => void saveAnswerToCourse(message)}>
                        {message.saved || t("notebook.saveToCourse")}
                      </button>
                    )}
                    {message.sources && message.sources.length > 0 && (
                      <div className="notebook__citations">
                        {message.sources.map((source, index) => (
                          <span key={`${message.id}-src-${index}`} className="notebook__citation">
                            [{index + 1}] {source.title}{source.pageNumber ? ` · ${t("notebook.page")} ${source.pageNumber}` : ""}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </article>
              ))}
              {busy && <p className="notebook__hint">{t("notebook.thinking")}</p>}
            </div>
            <div className="notebook__composer">
              <textarea
                className="nl-textarea notebook__input"
                rows={2}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                placeholder={t("notebook.inputPlaceholder")}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !matchMedia("(pointer: coarse)").matches) {
                    event.preventDefault();
                    void send();
                  }
                }}
              />
              <Button icon="send" disabled={busy || !draft.trim()} onClick={() => void send()}>{t("ai.send")}</Button>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}
